import { useQuery } from '@tanstack/react-query';
import { LIBRARY_BULK_MAX, type LibraryBulk, type LibraryFile, type LibraryListing, type LibraryVersion, type MediaKind } from '@kaba/shared';
import type { PreviewItem } from '../components/card/AttachmentPreview';
import { api } from './api';
import { queryClient } from './board';

/**
 * Dateibibliothek eines Boards (0.54) bzw. eines Teams (0.57). Abfragen unter ['library', schlüssel, …]
 * (Board-ID oder `team:<id>`); Änderungen anderer kommen als Ereignis `library.changed` an die Boards
 * und laden die offenen Ansichten neu (board.ts → applyEvent).
 *
 * - `board`: Board-Bibliothek
 * - `team`: Team-Bibliothek (Teameinstellungen)
 * - `boardTeam`: die für ein Board freigegebenen Ordner der Team-Bibliothek (nur lesen)
 */
export type LibraryScope = { kind: 'board'; boardId: string } | { kind: 'team'; teamId: string } | { kind: 'boardTeam'; boardId: string };

export function scopeBase(s: LibraryScope): string {
  return s.kind === 'board' ? `/api/boards/${s.boardId}/library` : s.kind === 'team' ? `/api/teams/${s.teamId}/library` : `/api/boards/${s.boardId}/team-library`;
}
/** Schlüssel für den Abfrage-Cache (Board-Ansicht der Team-Bibliothek hängt am Board) */
export const scopeKey = (s: LibraryScope): string => (s.kind === 'team' ? `team:${s.teamId}` : s.boardId);
const scopePart = (s: LibraryScope) => (s.kind === 'boardTeam' ? 'team' : 'own');
export interface LibraryQuery {
  folder: string | null;
  q: string;
  trash: boolean;
  type: MediaKind;
}

export function useLibrary(scope: LibraryScope, query: LibraryQuery, enabled = true) {
  return useQuery({
    queryKey: ['library', scopeKey(scope), scopePart(scope), query.folder, query.q, query.trash, query.type],
    queryFn: () => {
      const p = new URLSearchParams({ type: query.type });
      if (query.folder) p.set('folder', query.folder);
      if (query.q) p.set('q', query.q);
      if (query.trash) p.set('trash', '1');
      return api.get<LibraryListing>(`${scopeBase(scope)}?${p}`);
    },
    placeholderData: (prev) => prev,
    enabled,
  });
}

export interface FolderNode {
  id: string;
  parentId: string | null;
  name: string;
}

export function useLibraryFolders(scope: LibraryScope, enabled = true) {
  return useQuery({
    queryKey: ['library', scopeKey(scope), 'folders'],
    queryFn: () => api.get<FolderNode[]>(`${scopeBase(scope)}/folders`),
    enabled,
  });
}

/** Board-ID oder Bereich: lädt alle offenen Ansichten dieser Bibliothek neu */
export const refreshLibrary = (scope: string | LibraryScope) =>
  // Team-Bibliothek: auch die Board-Ansichten ihrer freigegebenen Ordner
  queryClient.invalidateQueries({ queryKey: typeof scope !== 'string' && scope.kind === 'team' ? ['library'] : ['library', typeof scope === 'string' ? scope : scopeKey(scope)] });

export async function uploadToLibrary(scope: LibraryScope, file: File, folderId: string | null): Promise<LibraryFile> {
  const form = new FormData();
  form.set('file', file);
  if (folderId) form.set('folderId', folderId);
  return api.upload<LibraryFile>(`${scopeBase(scope)}/files`, form);
}

/** Für die gemeinsame Vorschau (Bilder, PDF, Text) */
export function libraryPreviewItem(f: LibraryFile): PreviewItem {
  return {
    id: f.id,
    filename: f.name,
    mime: f.mime,
    size: f.size,
    isImage: f.isImage,
    url: f.url,
    thumbUrl: f.thumbUrl,
    previewUrl: f.previewUrl,
    previewWidth: f.previewWidth,
    width: f.width,
    height: f.height,
    downloadUrl: `/api/library/files/${f.id}/download`,
  };
}

export const sizeText = (b: number) => (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`);
export const dateText = (iso: string) => new Date(iso).toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' });

/** 0.55: Sammelaktion für mehrere Dateien (in Paketen zu LIBRARY_BULK_MAX) */
export async function bulkLibrary(scope: LibraryScope, action: LibraryBulk['action'], fileIds: string[], folderId?: string | null): Promise<void> {
  for (let i = 0; i < fileIds.length; i += LIBRARY_BULK_MAX) {
    await api.post(`${scopeBase(scope)}/bulk`, { action, fileIds: fileIds.slice(i, i + LIBRARY_BULK_MAX), ...(folderId !== undefined ? { folderId } : {}) });
  }
}

/** 0.55: neue Fassung einer Bibliotheksdatei hochladen */
export async function uploadLibraryVersion(fileId: string, file: File): Promise<LibraryFile> {
  const form = new FormData();
  form.set('file', file);
  return api.upload<LibraryFile>(`/api/library/files/${fileId}/versions`, form);
}

/**
 * 0.55: Mehrere Dateien als ZIP – als Formular-POST, damit der Browser selbst herunterlädt
 * (gestreamt, kein Zwischenspeicher im Tab). Grenzen prüft die Oberfläche vorher (Anzahl, Größe).
 */
export function downloadZip(scope: LibraryScope, ids: string[]): void {
  const form = document.createElement('form');
  form.method = 'post';
  form.action = `${scopeBase(scope)}/zip`;
  form.hidden = true;
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = 'ids';
  input.value = ids.join(',');
  form.append(input);
  document.body.append(form);
  form.submit();
  form.remove();
}

/** Grenze für das ZIP (ohne ZIP64) – wie der Server */
export const ZIP_MAX_BYTES = 3.5 * 1024 ** 3;

export function versionPreviewItem(v: LibraryVersion): PreviewItem {
  return {
    id: v.id,
    filename: `${v.name} (Fassung ${v.version})`,
    typeName: v.name,
    mime: v.mime,
    size: v.size,
    isImage: v.isImage,
    url: v.url,
    thumbUrl: v.thumbUrl,
    previewUrl: v.previewUrl,
    previewWidth: v.previewWidth,
    width: v.width,
    height: v.height,
    downloadUrl: v.downloadUrl,
  };
}
